import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import Ticket from "../flights/Ticket";
import { useAuth } from "../../context/AuthContext";
import { useBooking } from "../../context/BookingContext";
import {
  getBooking,
  downloadTicketPdf,
  sendTicketToPassenger,
  TicketSendResult,
} from "../../services/api";

const BookingConfirmed: React.FC = () => {
  const [searchParams] = useSearchParams();
  const { bookingId: contextBookingId } = useBooking();
  const bookingId = searchParams.get("bookingId") || contextBookingId;
  const [ticketData, setTicketData] = useState<any>(null);
  const [revalidatedData, setRevalidatedData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { user, loading: authLoading } = useAuth();
  const pnr = searchParams.get("pnr");
  const [downloading, setDownloading] = useState(false);
  const [downloadError, setDownloadError] = useState<string | null>(null);
  /**
   * Which channel is in flight, so only the button that was pressed shows a
   * spinner and the other stays usable. A single boolean would disable both.
   */
  const [sending, setSending] = useState<"email" | "whatsapp" | null>(null);
  const [sendResult, setSendResult] = useState<TicketSendResult | null>(null);
  const [sendError, setSendError] = useState<string | null>(null);

  /**
   * Save the e-ticket.
   *
   * The blob is fetched rather than linked to, because the route is behind the
   * session cookie and answers 404 for somebody else's booking — an <a href>
   * would show that 404 as a broken download with no explanation.
   *
   * The object URL is revoked straight after. Left behind it pins the whole PDF
   * in memory for as long as the tab lives.
   */
  const handleDownload = async () => {
    if (!bookingId || downloading) return;
    setDownloading(true);
    setDownloadError(null);
    try {
      const blob = await downloadTicketPdf(bookingId);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `eTicket-${pnr || bookingId}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err: any) {
      console.error("[BookingConfirmed] Ticket download failed:", err);
      setDownloadError(
        err?.response?.status === 409
          ? "The ticket is not ready yet. Try again in a moment."
          : "Could not prepare the ticket. Please try again.",
      );
    } finally {
      setDownloading(false);
    }
  };

  /**
   * Send it again, to the passenger's own email and WhatsApp.
   *
   * Ticketing already does this once. The button exists because that send can
   * fail for reasons nobody sees at the counter, and the customer is usually
   * still standing there.
   *
   * Each channel is reported separately — "sent" when only one worked is the
   * answer that gets somebody stuck at the airport.
   */
  const handleSend = async (channel: "email" | "whatsapp") => {
    if (!bookingId || sending) return;
    setSending(channel);
    setSendResult(null);
    setSendError(null);
    try {
      setSendResult(await sendTicketToPassenger(bookingId, channel));
    } catch (err: any) {
      console.error(`[BookingConfirmed] Ticket ${channel} send failed:`, err);
      setSendError(
        err?.response?.data?.error ?? "Could not send the ticket. Please try again.",
      );
    } finally {
      setSending(null);
    }
  };

  /**
   * One line per channel, saying where it went or why it did not.
   *
   * Returns null for a channel that was not asked for, so pressing "Email
   * ticket" does not also report on WhatsApp.
   */
  const describeChannel = (
    label: string,
    ch: { ok: boolean; to?: string; error?: string; skipped?: boolean } | undefined,
  ): string | null => {
    if (!ch) return null;
    if (ch.skipped && ch.error === "not requested") return null;
    if (ch.ok) return `${label} sent${ch.to ? ` to ${ch.to}` : ""}.`;
    if (ch.skipped) return `${label}: nothing on file to send to.`;
    return `${label} failed${ch.error ? ` — ${ch.error}` : ""}.`;
  };

  useEffect(() => {
    const fetchBooking = async () => {
      setLoading(true);
      setError(null);

      // Firebase restores the signed-in user asynchronously, so on a fresh page
      // load — which is exactly how this link is opened, straight from an email
      // or a message — `user` is null for a moment before it arrives. Reading
      // that as "no user" is what put a red error on screen while the page was
      // still perfectly fine: the effect refused, turned loading off, and the
      // real loading screen below was never reached. Stay on it until auth has
      // actually settled, then decide.
      if (authLoading) return;

      if (!pnr) {
        setError("This link is missing its booking reference (PNR). Please open it again from your confirmation.");
        setLoading(false);
        return;
      }
      if (!bookingId) {
        setError("This link is missing its booking id. Please open it again from your confirmation.");
        setLoading(false);
        return;
      }
      // Said plainly, because it is the likeliest reason somebody lands here
      // and sees nothing: the link works, they are just not signed in yet.
      if (!user?.uid) {
        setError("Please sign in to see this booking.");
        setLoading(false);
        return;
      }

      try {
        let bookingDoc: Record<string, any>;

        try {
          bookingDoc = await getBooking(user.uid, bookingId);
        } catch (firstErr: any) {
          // Booking might not be saved yet (race condition) — wait 1s and retry once
          if (firstErr?.response?.status === 404) {
            console.log("[BookingConfirmed] Booking not found, retrying in 1s...");
            await new Promise((resolve) => setTimeout(resolve, 1000));
            try {
              bookingDoc = await getBooking(user.uid, bookingId);
            } catch (retryErr: any) {
              setError("Booking not found. Please check your booking ID or try again.");
              setLoading(false);
              return;
            }
          } else {
            throw firstErr;
          }
        }

        // Verify PNR matches for security
        if (bookingDoc.pnr && bookingDoc.pnr !== pnr) {
          setError("PNR mismatch. Please verify your booking.");
          setLoading(false);
          return;
        }

        // Verify booking is processed successfully
        if (bookingDoc.ticketingStatus !== "success" && !bookingDoc.pnr) {
          setError("Booking is still being processed. Please wait a moment and refresh.");
          setLoading(false);
          return;
        }

        setTicketData(bookingDoc.finalBooking || bookingDoc);
        setRevalidatedData(bookingDoc.revalidatedData || null);
      } catch (err: any) {
        console.error("[BookingConfirmed] Error fetching booking:", err);
        setError("Failed to fetch ticket details. Please try again.");
      }
      setLoading(false);
    };

    fetchBooking();
  }, [bookingId, user?.uid, pnr, authLoading]);

  // Helper to get flights for a journey
  function getFlightsForJourney(
    flights: any[],
    journeyIdx: number,
    journeys: any[],
  ) {
    let start = 0;
    for (let i = 0; i < journeyIdx; i++) {
      start += journeys[i].numberOfFlights;
    }
    const count = journeys[journeyIdx].numberOfFlights;
    return flights.slice(start, start + count);
  }

  // Extract seat number from the Sabre finalBooking response for a given
  // traveler and journey. Sabre returns seats ordered by traveler within each
  // flight's seats[] array (first seat → first traveler, etc.).
  function getSeatForTraveler(
    flights: any[],
    journeys: any[],
    travelerIdx: number,
    journeyIdx: number,
  ): string | undefined {
    if (!Array.isArray(flights) || !Array.isArray(journeys)) return undefined;
    let start = 0;
    for (let i = 0; i < journeyIdx; i++) {
      start += journeys[i]?.numberOfFlights || 0;
    }
    const firstFlight = flights[start];
    const seats = firstFlight?.seats;
    if (!Array.isArray(seats) || seats.length === 0) return undefined;
    // Sabre orders seats by NameNumber — index matches traveler position
    return seats[travelerIdx]?.number;
  }

  // Sabre nests the e-ticket number inside accountingItems; surface it at
  // the root so both the mapped-traveler and fallback paths can reference it.
  const ticketNumber =
    ticketData?.accountingItems?.[0]?.ticketNumber ||
    ticketData?.flightTickets?.[0]?.number;

  const paymentTotals = ticketData?.payments?.flightCurrentTotals?.[0];

  const currency = paymentTotals?.currencyCode || "AED";
  const basefare = Number(paymentTotals?.subtotal) || 0;
  const taxes = Number(paymentTotals?.taxes) || 0;
  const total = Number(paymentTotals?.total) || 0;

  if (loading)
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-4 font-sora">
        <div
          className="h-10 w-10 animate-spin rounded-full border-4 border-gray-200 border-t-primary"
          role="status"
          aria-label="Loading your ticket"
        />
        <div className="text-lg text-primary">Loading your ticket…</div>
        <div className="text-sm text-gray-400">This takes a moment while we fetch the booking.</div>
      </div>
    );
  if (error)
    return (
      <div className="min-h-screen flex items-center justify-center text-xl text-red-500 font-sora">
        {error}
      </div>
    );
  if (!ticketData)
    return (
      <div className="min-h-screen flex items-center justify-center text-xl text-primary font-sora">
        No ticket data found.
      </div>
    );

  return (
    <div className="min-h-screen bg-white flex flex-col items-center py-8 font-sans">
      <h1 className="text-3xl md:text-4xl font-bold text-[#1A3399] mb-2 flex items-center gap-2">
        Booking Confirmed
        <span>
          <svg width="28" height="28" fill="none" viewBox="0 0 24 24">
            <circle cx="12" cy="12" r="12" fill="#22c55e" />
            <path
              d="M7 13l3 3 7-7"
              stroke="#fff"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </span>
      </h1>

      {/* The passenger's own copy. It is also emailed and sent over WhatsApp,
          but neither is certain to arrive — a wrong address, a closed WhatsApp
          window — and somebody standing at the counter should not have to wait
          to find out. */}
      <div className="w-full flex flex-col items-center mb-6">
        <div className="flex flex-wrap items-center justify-center gap-3">
          <button
            type="button"
            onClick={handleDownload}
            disabled={downloading || !bookingId}
            className="inline-flex items-center gap-2 rounded-lg bg-[#1A3399] px-5 py-2.5 text-sm font-semibold text-white transition hover:bg-blue-800 disabled:opacity-50"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <path
                d="M12 3v12m0 0l-4-4m4 4l4-4M4 17v2a2 2 0 002 2h12a2 2 0 002-2v-2"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            {downloading ? "Preparing…" : "Download e-ticket (PDF)"}
          </button>

          {/* Secondary styling on purpose: the ticket is already sent
              automatically when it is issued, so these are the "it did not
              arrive" buttons rather than the expected next step.

              One per channel, because the two fail for entirely unrelated
              reasons — a wrong address, a closed WhatsApp window — and fixing
              one should not mean re-sending the other. */}
          <button
            type="button"
            onClick={() => handleSend("email")}
            disabled={sending !== null || !bookingId}
            className="inline-flex items-center gap-2 rounded-lg border border-[#1A3399] px-5 py-2.5 text-sm font-semibold text-[#1A3399] transition hover:bg-blue-50 disabled:opacity-50"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <path
                d="M3 6l9 6 9-6M3 6h18v12H3V6z"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            {sending === "email" ? "Sending…" : "Email ticket"}
          </button>

          <button
            type="button"
            onClick={() => handleSend("whatsapp")}
            disabled={sending !== null || !bookingId}
            className="inline-flex items-center gap-2 rounded-lg border border-green-600 px-5 py-2.5 text-sm font-semibold text-green-700 transition hover:bg-green-50 disabled:opacity-50"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
              <path d="M12.04 2C6.58 2 2.13 6.45 2.13 11.91c0 1.75.46 3.45 1.32 4.95L2 22l5.25-1.38a9.9 9.9 0 004.79 1.22h.01c5.46 0 9.9-4.45 9.9-9.91C21.95 6.45 17.5 2 12.04 2zm5.8 14.16c-.24.68-1.2 1.26-1.97 1.42-.53.11-1.22.2-3.54-.76-2.97-1.23-4.88-4.24-5.03-4.44-.14-.2-1.2-1.6-1.2-3.05 0-1.45.76-2.16 1.03-2.46.27-.3.58-.37.78-.37.19 0 .39 0 .56.01.18.01.42-.07.66.5.24.58.82 2.02.89 2.17.07.15.12.32.02.52-.1.2-.15.32-.3.5-.14.17-.3.38-.43.51-.14.15-.29.3-.13.59.17.29.74 1.22 1.59 1.98 1.09.97 2.01 1.27 2.3 1.42.29.15.46.12.63-.07.17-.2.72-.84.91-1.13.19-.29.38-.24.64-.15.26.1 1.68.79 1.97.94.29.15.48.22.55.34.07.12.07.7-.17 1.38z" />
            </svg>
            {sending === "whatsapp" ? "Sending…" : "Send to WhatsApp"}
          </button>
        </div>

        {downloadError && (
          <p className="mt-2 text-sm text-red-600">{downloadError}</p>
        )}
        {sendError && <p className="mt-2 text-sm text-red-600">{sendError}</p>}

        {sendResult && (
          <div
            className={`mt-3 rounded-lg border px-4 py-2 text-sm ${
              sendResult.email.ok || sendResult.whatsapp.ok
                ? "border-green-200 bg-green-50 text-green-800"
                : "border-red-200 bg-red-50 text-red-700"
            }`}
          >
            {[
              describeChannel("Email", sendResult.email),
              describeChannel("WhatsApp", sendResult.whatsapp),
            ]
              .filter((line): line is string => line !== null)
              .map((line) => (
                <p key={line}>{line}</p>
              ))}
          </div>
        )}
      </div>

      <div className="w-full flex flex-col items-center">
        {Array.isArray(ticketData.travelers) &&
          Array.isArray(ticketData.journeys) ? (
          ticketData.travelers.map((traveler: any, tIdx: number) =>
            ticketData.journeys.map((journey: any, jIdx: number) => {
              const journeyFlights = getFlightsForJourney(
                ticketData.flights,
                jIdx,
                ticketData.journeys,
              );
              const firstFlight = journeyFlights[0];
              const lastFlight = journeyFlights[journeyFlights.length - 1];

              return (
                <Ticket
                  key={`${traveler.nameAssociationId || tIdx}-${jIdx}`}
                  flightData={{
                    onwardFlight: {
                      departureAirport: firstFlight.fromAirportCode,
                      arrivalAirport: lastFlight.toAirportCode,
                      schedules: journeyFlights.map((f) => ({
                        flightNumber: f.flightNumber,
                        // Handle both durationInMinutes and elapsedTime fields
                        elapsedTime: f.durationInMinutes || f.elapsedTime,
                        departure: f.departureDate,
                        departureTime: f.departureTime,
                        arrival: f.arrivalDate,
                        arrivalTime: f.arrivalTime,
                        cabinTypeName: f.cabinTypeName,
                        stopCount: journey.numberOfFlights,
                        airlineCode: f.airlineCode,
                        departureAirport: f.fromAirportCode,
                        arrivalAirport: f.toAirportCode,
                      })),
                      departureTerminalName: firstFlight.departureTerminalName,
                      arrivalTerminalName: lastFlight.arrivalTerminalName,
                      bookingDate: ticketData.creationDetails?.creationDate,
                    },
                    validatingCarrier: firstFlight.airlineCode,
                    bookingId: bookingId,
                    pnr: pnr,
                    ticketNumber,
                    totalFare: {
                      amount: ticketData.totalAmount,
                      currency: ticketData.currency,
                      baseFare: ticketData.totalAmount,
                      taxes: 0,
                    },
                    revalidatedData,
                    specialServices: ticketData.specialServices,
                  }}
                  passenger={{
                    firstName: traveler.givenName,
                    lastName: traveler.surname,
                    seat: getSeatForTraveler(ticketData.flights, ticketData.journeys, tIdx, jIdx),
                  }}
                  journey={{
                    ...journey,
                    departureDate: firstFlight.departureDate,
                    departureTime: firstFlight.departureTime,
                    arrivalDate: lastFlight.arrivalDate,
                    arrivalTime: lastFlight.arrivalTime,
                  }}
                  ticketNumber={ticketNumber}
                />
              );
            }),
          )
        ) : (
          <Ticket
            flightData={{
              onwardFlight: {
                departureAirport: ticketData.flights?.[0]?.fromAirportCode,
                arrivalAirport: ticketData.flights?.[0]?.toAirportCode,
                schedules: [
                  {
                    flightNumber: ticketData.flights?.[0]?.flightNumber,
                    // Handle both durationInMinutes and elapsedTime fields
                    elapsedTime: ticketData.flights?.[0]?.elapsedTime || ticketData.flights?.[0]?.durationInMinutes,
                    stopCount: 0,
                    departure: ticketData.flights?.[0]?.departureDate,
                    departureTime: ticketData.flights?.[0]?.departureTime,
                    arrival: ticketData.flights?.[0]?.arrivalDate,
                    arrivalTime: ticketData.flights?.[0]?.arrivalTime,
                    cabinTypeName: ticketData.flights?.[0]?.cabinTypeName,
                    departureAirport: ticketData.flights?.[0]?.fromAirportCode,
                    arrivalAirport: ticketData.flights?.[0]?.toAirportCode,
                    airlineCode: ticketData.flights?.[0]?.airlineCode,
                  },
                ],
                departureTerminalName:
                  ticketData.flights?.[0]?.departureTerminalName,
                arrivalTerminalName:
                  ticketData.flights?.[0]?.arrivalTerminalName,
                bookingDate: ticketData.creationDetails?.creationDate,
              },
              validatingCarrier: ticketData.flights?.[0]?.airlineCode,
              bookingId: bookingId,
              pnr: pnr,
              ticketNumber,
              totalFare: {
                amount: ticketData.totalAmount,
                currency: ticketData.currency,
                baseFare: ticketData.totalAmount,
                taxes: 0,
              },
              revalidatedData,
              specialServices: ticketData.specialServices,
            }}
            passenger={{
              firstName: ticketData.firstName,
              lastName: ticketData.lastName,
              seat: ticketData.flights?.[0]?.seats?.[0]?.number,
            }}
            journey={ticketData.journeys?.[0]}
            ticketNumber={ticketNumber}
          />
        )}
      </div>

      {/* Fare Summary and Hotel Cards */}
      <div className="w-full max-w-4xl flex flex-col md:flex-row gap-4 mt-8">
        {/* Fare Summary Card */}
        <div className="flex-1 bg-[#1A3399] rounded-2xl p-6 text-white text-left shadow-md">
          <div className="text-2xl font-bold mb-4">Fare Summary</div>
          <div className="flex flex-col gap-2">
            <div className="flex justify-between items-center">
              <span>Base Fare</span>
              <span className="font-semibold">
                {currency} {basefare}
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span>Taxes</span>
              <span className="font-semibold">
                {currency} {taxes}
              </span>
            </div>
            <div className="flex justify-between items-center border-white pt-2 mt-2">
              <span className="text-lg font-bold">Total</span>
              <span className="text-lg font-bold">
                {currency} {total}
              </span>
            </div>
          </div>
        </div>
        {/* Hotel Card */}
        <div className="flex-1 bg-gradient-to-r from-[#1A3399] to-[#3b82f6] rounded-2xl p-6 text-white flex flex-col items-center justify-center">
          <div className="text-lg font-semibold mb-2">
            Book Hotels in Abu Dhabi
          </div>
          <svg width="40" height="40" fill="none" viewBox="0 0 24 24">
            <rect x="3" y="10" width="18" height="10" rx="2" fill="#fff" />
            <rect x="7" y="14" width="2" height="2" fill="#1A3399" />
            <rect x="11" y="14" width="2" height="2" fill="#1A3399" />
            <rect x="15" y="14" width="2" height="2" fill="#1A3399" />
            <rect x="7" y="18" width="10" height="2" fill="#1A3399" />
          </svg>
        </div>
        <div className="flex-1 bg-gradient-to-r from-[#1A3399] to-[#3b82f6] rounded-2xl p-6 text-white flex flex-col items-center justify-center">
          <div className="text-lg font-semibold mb-2">
            Save on return tickets to Kochi
          </div>
          <svg width="40" height="40" fill="none" viewBox="0 0 24 24">
            <rect x="4" y="8" width="16" height="8" rx="2" fill="#fff" />
            <path
              d="M8 12h8M12 8v8"
              stroke="#1A3399"
              strokeWidth="2"
              strokeLinecap="round"
            />
            <path
              d="M16 16l2 2"
              stroke="#1A3399"
              strokeWidth="2"
              strokeLinecap="round"
            />
          </svg>
        </div>
      </div>
    </div>
  );
};

export default BookingConfirmed;